import { AnalysisPreset, Visualisation, AtomVisualisation, AtomComparison, PARENT_ANALYSIS_TYPE } from 'domain/entity/analysis';
import { Option, none, isNone, some, isSome } from 'fp-ts/lib/Option';

export interface BasicAnalysisDataCriteria {
    resolution: string;
    period: string;
    presets: AnalysisPreset[];
    datasources: [];
    formulas: [];
    compareTimeshift: string;
    visualisation: Visualisation;
    atomsVisualisation: AtomVisualisation[];
    timeShifts: number;
    comparisons: AtomComparison[];
    compareAlignment: string;
    title: string | undefined;
}

export interface AnalysisDataCriteria extends BasicAnalysisDataCriteria {
    analysisId: string;
    saveChanges: boolean;
}

export interface CreateAnalysisDataCriteria extends BasicAnalysisDataCriteria {
    parentId: string;
    parentType: PARENT_ANALYSIS_TYPE;
}

class AnalysisDataCriteriaImpl implements AnalysisDataCriteria {
    datasources: [];

    formulas: [];

    comparisons: [];

    constructor(
        readonly analysisId: string,
        readonly resolution: string,
        readonly period: string,
        readonly presets: AnalysisPreset[],
        readonly compareTimeshift: string,
        readonly visualisation: Visualisation,
        readonly atomsVisualisation: AtomVisualisation[],
        readonly timeShifts: number,
        readonly compareAlignment: string,
        readonly title: string | undefined,
        readonly saveChanges: boolean
    ) {
        this.datasources = [];
        this.formulas = [];
        this.comparisons = [];
    }
}

// Implement the CreateAnalysisDataCriteriaImpl class
class CreateAnalysisDataCriteriaImpl implements CreateAnalysisDataCriteria {
    datasources: [];

    formulas: [];

    comparisons: [];

    constructor(
        readonly parentId: string,

        readonly parentType: PARENT_ANALYSIS_TYPE,

        readonly resolution: string,

        readonly period: string,

        readonly presets: AnalysisPreset[],

        readonly compareTimeshift: string,

        readonly visualisation: Visualisation,

        readonly atomsVisualisation: AtomVisualisation[],

        readonly timeShifts: number,

        readonly compareAlignment: string,

        readonly title: string | undefined
    ) {
        this.datasources = [];
        this.formulas = [];
        this.comparisons = [];
    }
}

// Utility function to build shared properties
const buildCommonProperties = (
    resolution: Option<string>,
    period: Option<string>,
    presets: Option<AnalysisPreset[]>,
    compareTimeshift: Option<string>,
    visualisation: Option<Visualisation>,
    atomsVisualisation: Option<AtomVisualisation[]>,
    timeShifts: Option<number>,
    compareAlignment: Option<string>,
    title: Option<string>
) => {
    if (
        isNone(resolution) ||
        isNone(period) ||
        isNone(presets) ||
        isNone(compareTimeshift) ||
        isNone(visualisation) ||
        isNone(atomsVisualisation) ||
        isNone(compareAlignment)
    ) {
        throw new ReferenceError('Undefined prop');
    }

    return {
        resolution: resolution.value,
        period: period.value,
        presets: presets.value,
        compareTimeshift: compareTimeshift.value,
        visualisation: visualisation.value,
        atomsVisualisation: atomsVisualisation.value,
        timeShifts: isSome(timeShifts) ? timeShifts.value : 0,
        compareAlignment: compareAlignment.value,
        title: isSome(title) ? title.value : undefined,
    };
};

// Implement the AnalysisDataCriteriaBuilder class
export class AnalysisDataCriteriaBuilder {
    private analysisId: Option<string> = none;

    private resolution: Option<string> = none;

    private period: Option<string> = none;

    private compareTimeshift: Option<string> = none;

    private presets: Option<AnalysisPreset[]> = none;

    private visualisation: Option<Visualisation> = none;

    private atomsVisualisation: Option<AtomVisualisation[]> = none;

    private timeShifts: Option<number> = none;

    private compareAlignment: Option<string> = none;

    private title: Option<string> = none;

    private saveChanges: Option<boolean> = none;

    static create = (): AnalysisDataCriteriaBuilder => new AnalysisDataCriteriaBuilder();

    withAnalysisId = (analysisId: string): AnalysisDataCriteriaBuilder => {
        this.analysisId = some(analysisId);
        return this;
    };

    withResolution = (resolution: string): AnalysisDataCriteriaBuilder => {
        this.resolution = some(resolution);
        return this;
    };

    withPeriod = (period: string): AnalysisDataCriteriaBuilder => {
        this.period = some(period);
        return this;
    };

    withCompareTimeshiftAndAlignment = (compareTimeshift: string): AnalysisDataCriteriaBuilder => {
        this.compareTimeshift = some(compareTimeshift.replace('_WITH_WEEKDAY', ''));
        this.compareAlignment = some(compareTimeshift.includes('_WITH_WEEKDAY') ? 'WEEKDAY' : 'NONE');
        return this;
    };

    withPresets = (presets: AnalysisPreset[]): AnalysisDataCriteriaBuilder => {
        this.presets = some(presets);
        return this;
    };

    withVisualisation = (visualisation: Visualisation): AnalysisDataCriteriaBuilder => {
        this.visualisation = some(visualisation);
        return this;
    };

    withAtomsVisualisation = (atomsVisualisation: AtomVisualisation[]): AnalysisDataCriteriaBuilder => {
        this.atomsVisualisation = some(atomsVisualisation);
        return this;
    };

    withTimeShifts = (shifts: number): AnalysisDataCriteriaBuilder => {
        this.timeShifts = some(shifts);
        return this;
    };

    withTitle = (title: string): AnalysisDataCriteriaBuilder => {
        this.title = some(title);
        return this;
    };

    withSaveChanges = (saveChanges: boolean): AnalysisDataCriteriaBuilder => {
        this.saveChanges = some(saveChanges);
        return this;
    };

    build = (): AnalysisDataCriteria => {
        if (isNone(this.analysisId)) {
            throw new ReferenceError('Undefined prop');
        }

        const commonProps = buildCommonProperties(
            this.resolution,
            this.period,
            this.presets,
            this.compareTimeshift,
            this.visualisation,
            this.atomsVisualisation,
            this.timeShifts,
            this.compareAlignment,
            this.title
        );

        return new AnalysisDataCriteriaImpl(
            this.analysisId.value,
            commonProps.resolution,
            commonProps.period,
            commonProps.presets,
            commonProps.compareTimeshift,
            commonProps.visualisation,
            commonProps.atomsVisualisation,
            commonProps.timeShifts,
            commonProps.compareAlignment,
            commonProps.title,
            isSome(this.saveChanges) ? this.saveChanges.value : false
        );
    };
}

// Implement the CreateAnalysisDataCriteriaBuilder class
export class CreateAnalysisDataCriteriaBuilder {
    private parentId: Option<string> = none;

    private parentType: Option<PARENT_ANALYSIS_TYPE> = none;

    private resolution: Option<string> = none;

    private period: Option<string> = none;

    private compareTimeshift: Option<string> = none;

    private presets: Option<AnalysisPreset[]> = none;

    private visualisation: Option<Visualisation> = none;

    private atomsVisualisation: Option<AtomVisualisation[]> = none;

    private timeShifts: Option<number> = none;

    private compareAlignment: Option<string> = none;

    private title: Option<string> = none;

    static create = (): CreateAnalysisDataCriteriaBuilder => new CreateAnalysisDataCriteriaBuilder();

    withParentId = (parentId: string): CreateAnalysisDataCriteriaBuilder => {
        this.parentId = some(parentId);
        return this;
    };

    withResolution = (resolution: string): CreateAnalysisDataCriteriaBuilder => {
        this.resolution = some(resolution);
        return this;
    };

    withPeriod = (period: string): CreateAnalysisDataCriteriaBuilder => {
        this.period = some(period);
        return this;
    };

    withCompareTimeshiftAndAlignment = (compareTimeshift: string): CreateAnalysisDataCriteriaBuilder => {
        this.compareTimeshift = some(compareTimeshift.replace('_WITH_WEEKDAY', ''));
        this.compareAlignment = some(compareTimeshift.includes('_WITH_WEEKDAY') ? 'WEEKDAY' : 'NONE');
        return this;
    };

    withPresets = (presets: AnalysisPreset[]): CreateAnalysisDataCriteriaBuilder => {
        this.presets = some(presets);
        return this;
    };

    withVisualisation = (visualisation: Visualisation): CreateAnalysisDataCriteriaBuilder => {
        this.visualisation = some(visualisation);
        return this;
    };

    withAtomsVisualisation = (atomsVisualisation: AtomVisualisation[]): CreateAnalysisDataCriteriaBuilder => {
        this.atomsVisualisation = some(atomsVisualisation);
        return this;
    };

    withTimeShifts = (shifts: number): CreateAnalysisDataCriteriaBuilder => {
        this.timeShifts = some(shifts);
        return this;
    };

    withTitle = (title: string): CreateAnalysisDataCriteriaBuilder => {
        this.title = some(title);
        return this;
    };

    withParentType = (parentType: PARENT_ANALYSIS_TYPE): CreateAnalysisDataCriteriaBuilder => {
        this.parentType = some(parentType);
        return this;
    };

    build = (): CreateAnalysisDataCriteria => {
        if (isNone(this.parentId) || isNone(this.parentType)) {
            throw new ReferenceError('Undefined prop');
        }

        const commonProps = buildCommonProperties(
            this.resolution,
            this.period,
            this.presets,
            this.compareTimeshift,
            this.visualisation,
            this.atomsVisualisation,
            this.timeShifts,
            this.compareAlignment,
            this.title
        );

        return new CreateAnalysisDataCriteriaImpl(
            this.parentId.value,
            this.parentType.value,
            commonProps.resolution,
            commonProps.period,
            commonProps.presets,
            commonProps.compareTimeshift,
            commonProps.visualisation,
            commonProps.atomsVisualisation,
            commonProps.timeShifts,
            commonProps.compareAlignment,
            commonProps.title
        );
    };
}

export default AnalysisDataCriteria;
